import { useMemo, useCallback } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { TDownloadFile, TDownloadFileQueue } from '../../ducks/download-panel/types-download';

/**
 * Hook para obtener las columnas del panel de descargas.
 */
export const useDownloadPanelColumns = () => {
  const { t } = useMEMTranslation();
  return useMemo(
    () => [
      {
        field: 'fileName',
        content: t('downloadPanelFileName'),
      },
      {
        field: 'status',
        content: t('downloadPanelStatus'),
      },
      {
        field: 'date',
        content: 'Date',
      },
    ],
    [t]
  );
};

/**
 * Hook para obtener la función que traduce el status de descarga.
 */
export const useDownloadStatusLabel = () => {
  const { t } = useMEMTranslation();
  return useCallback(
    (status: string) => {
      switch (status) {
        case 'QUEUE_COMPLETED':
          return t('downloadPanelStatusDownloading');
        case 'BEGIN':
          return t('downloadPanelStatusDownloading');
        case 'COMPLETED':
          return t('downloadPanelStatusCompleted');
        case 'ERROR_DOWNLOAD':
          return t('downloadPanelStatusError');
        case 'ERROR':
          return t('downloadPanelStatusError');
        case 'NO_DATA_FOUND':
          return t('downloadPanelStatusNoDataFound');
        case 'PARTIALLY_COMPLETED':
          return t('downloadPanelStatusError');
        default:
          return status;
      }
    },
    [t]
  );
};

/**
 * Hook para mapear los archivos de descarga a filas para la tabla.
 */
export const useDownloadPanelRows = (
  downloadsQueue: TDownloadFileQueue[] | undefined,
  downloads: TDownloadFile[],
  getStatusLabel: (status: string) => string
) => {
  return useMemo(() => {
    const allFiles: any[] = [];
    if (downloadsQueue && Array.isArray(downloadsQueue)) {
      allFiles.push(...downloadsQueue);
    }
    if (downloads && Array.isArray(downloads)) {
      allFiles.push(...downloads);
    }
    return allFiles.map((file: any, idx: number) => ({
      id: file.id || idx,
      cells: {
        fileName: file.fileName,
        status: getStatusLabel(file.status),
        date: file.date
          ? new Date(file.date).toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
      },
      customData: file,
    }));
  }, [downloadsQueue, downloads, getStatusLabel]);
};
